import { useEffect, useState } from "react";
import { api, type Stats } from "../api";

const RANGES = [7, 30, 90] as const;

function fmtTRY(kurus: number): string {
  return (kurus / 100).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  });
}

function fmtMs(ms: number | null): string {
  if (ms === null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

function pct(n: number | null): string {
  return n === null ? "—" : `${(n * 100).toFixed(1)}%`;
}

export function StatsTab() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setErr(null);
    api
      .stats(days)
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e instanceof Error ? e.message : "load_failed"));
    return () => {
      live = false;
    };
  }, [days]);

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2>Usage statistics</h2>
        <div className="row" style={{ flex: "0 0 auto", gap: 6 }}>
          {RANGES.map((r) => (
            <button
              key={r}
              className={`tab ${days === r ? "active" : ""}`}
              onClick={() => setDays(r)}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {err && <div className="error">{err}</div>}
      {!data && !err && <div className="muted">Loading…</div>}

      {data && (
        <>
          <div className="stat-grid">
            <Stat label="Verified (lifetime)" value={data.totals.lifetime_verified.toLocaleString()} />
            <Stat
              label={`Verified (${days}d)`}
              value={data.funnel.verified.toLocaleString()}
              sub={`${pct(data.funnel.success_rate)} success`}
            />
            <Stat label="Issued (window)" value={data.funnel.issued.toLocaleString()} />
            <Stat label="Avg time-to-verify" value={fmtMs(data.health.avg_verify_ms)} />
            <Stat label="Webapps" value={`${data.totals.active_webapps} / ${data.totals.webapps}`} sub="active / total" />
            <Stat label="Customers" value={data.totals.customers.toLocaleString()} />
            <Stat
              label="Revenue (paid)"
              value={fmtTRY(data.revenue.gross_kurus)}
              sub={`${data.revenue.paid_orders} orders`}
            />
            <Stat
              label="Credits"
              value={data.totals.credits_consumed.toLocaleString()}
              sub={`${data.totals.credits_remaining.toLocaleString()} remaining`}
            />
          </div>

          <h3 className="stat-h">Verification funnel · last {days} days</h3>
          {data.daily.length === 0 ? (
            <div className="muted">No data in this range yet.</div>
          ) : (
            <DailyChart daily={data.daily} />
          )}

          <h3 className="stat-h">Operational health</h3>
          <div className="stat-grid">
            <Stat label="Inbound SMS" value={data.health.inbound_total.toLocaleString()} />
            <Stat label="Matched" value={data.health.inbound_matched.toLocaleString()} tone="ok" />
            <Stat label="Unmatched" value={data.health.inbound_unmatched.toLocaleString()} />
            <Stat
              label="Callback errors"
              value={data.health.callback_errors.toLocaleString()}
              tone={data.health.callback_errors > 0 ? "bad" : undefined}
            />
            <Stat
              label="Forbidden probes"
              value={data.health.forbidden.toLocaleString()}
              tone={data.health.forbidden > 0 ? "bad" : undefined}
            />
            <Stat label="Timeouts" value={data.funnel.timeout.toLocaleString()} />
          </div>

          <div className="stat-cols">
            <div>
              <h3 className="stat-h">Top webapps</h3>
              {data.top_webapps.length === 0 ? (
                <div className="muted">No usage yet.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Issued</th>
                        <th>Verified</th>
                        <th>Timeout</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_webapps.map((w) => (
                        <tr key={w.webapp_id}>
                          <td data-label="Code">
                            <code>{w.code ?? "—"}</code>
                          </td>
                          <td data-label="Name">{w.name ?? <span className="muted">deleted</span>}</td>
                          <td data-label="Issued">{w.issued.toLocaleString()}</td>
                          <td data-label="Verified">{w.verified.toLocaleString()}</td>
                          <td data-label="Timeout">{w.timeout.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h3 className="stat-h">Top customers</h3>
              {data.top_customers.length === 0 ? (
                <div className="muted">No customer usage yet.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Verified ({days}d)</th>
                        <th>Credits used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_customers.map((cu) => (
                        <tr key={cu.id}>
                          <td data-label="Customer">{cu.email}</td>
                          <td data-label="Verified">{cu.verified.toLocaleString()}</td>
                          <td data-label="Credits used">{cu.consumed_credits.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <p className="muted stat-note">
            Aggregated from non-personal rollups (no phone, IP or message content);
            session and webhook rows are deleted within the KVKK retention window.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "bad";
}) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={`stat-num ${tone ?? ""}`}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function DailyChart({ daily }: { daily: Stats["daily"] }) {
  const max = Math.max(1, ...daily.map((d) => d.issued));
  return (
    <div className="chart">
      {daily.map((d) => {
        const h = (n: number) => `${(n / max) * 100}%`;
        const title = `${d.day} · issued ${d.issued}, verified ${d.verified}, timeout ${d.timeout}, failed ${d.failed}`;
        return (
          <div className="chart-col" key={d.day} title={title}>
            <div className="chart-bar">
              <div className="seg ok" style={{ height: h(d.verified) }} />
              <div className="seg warn" style={{ height: h(d.timeout) }} />
              <div className="seg bad" style={{ height: h(d.failed) }} />
            </div>
            <div className="chart-x">{d.day.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}
